import BigNumber from 'bignumber.js';
import isEmpty from 'lodash/isEmpty';
import map from 'lodash/map';
import omitBy from 'lodash/omitBy';
import round from 'lodash/round';
import uniq from 'lodash/uniq';
import uniqBy from 'lodash/uniqBy';

/**
 * 审批状态枚举
 *
 * 该数组定义了审批流程中可能的状态及其对应的值。
 * 每个对象包含一个`label`字段用于显示状态的文本描述，
 * 和一个`value`字段用于表示状态的唯一标识。
 *
 */
export const auditStatus = [
  {
    label: '待审核',
    value: '10',
  },
  {
    label: '同意',
    value: '20',
  },
  {
    label: '不同意',
    value: '30',
  },
];
/**
 * 待调整货量枚举
 *
 * 此数组定义了三种调整权重的状态，每种状态用一个对象表示，
 * 对象包含`label`和`value`两个属性，分别表示状态的显示名称和对应的状态值。
 * 这种表示方式便于在用户界面中展示这些状态，并且在后台处理时可以根据状态值进行相应的逻辑处理。
 *
 * @constant
 * @type {Array}
 * @property {string} label - 状态的显示名称。
 * @property {string} value - 状态对应的状态值。
 */
export const adjustWeightStatus = [
  {
    label: '未调整',
    value: '10',
  },
  {
    label: '全部调整',
    value: '20',
  },
  {
    label: '部分调整',
    value: '30',
  },
];

/**
 * 通用查找方法
 *
 * 该函数用于从给定的枚举数组中根据`value`或`label`查找并返回匹配的对象。
 * 如果没有找到匹配项，将返回`undefined`。
 *
 * @param {Array} type - 包含多个枚举对象的数组。
 * @param {string} key - 要查找的键名，可以是'value'或'label'。
 * @param {string} value - 要查找的键值。
 * @returns {Object|undefined} - 找到的枚举对象或undefined。
 */
export function findInEnums(enums = [], key, value) {
  return enums.find(enumItem => enumItem[key] === value);
}
/**
 * 根据当前环境决定模块导出的路径。
 *
 * 本模块的导出路径取决于NODE_ENV环境变量的值。在生产环境中，路径会被设置为'/mdp-station-keyword'，
 * 以加载生产环境中特定的模块。在非生产环境中（如开发环境），路径将为空字符串，表示使用默认的
 * 模块路径，或者不加载特定于生产的模块。
 *
 */
export const module = `${process.env.NODE_ENV === 'production' ? '/mdp-station-keyword' : ''}`;

/**
 * 生成列配置对象数组
 *
 * @param name 列名称的前缀
 * @returns 包含列配置对象的数组
 */
export const mapColumn = name => {
  return [
    {
      title: `${name}编号`,
      dataIndex: 'code',
    },
    {
      title: `${name}名称`,
      dataIndex: 'name',
    },
  ];
};


/**
 * 使用async/await语法对表单进行验证并获取表单数据
 *
 * @param {Object} current - 当前表单的引用对象，通常来自于Ant Design的Form.useForm()
 * @returns {Object|boolean} 如果表单验证通过，则返回表单数据对象；如果验证失败，则返回false
 *
 * 该函数首先检查传入的current对象是否存在，并且是否拥有validateFieldsAndScroll方法。
 * 如果条件满足，则调用validateFieldsAndScroll方法对表单进行验证，并等待其异步完成。
 * 验证的回调函数接收两个参数：errs（错误对象数组）和vals（表单数据对象）。
 * 如果存在验证错误（errs），则返回false；否则，返回表单数据对象（vals）。
 * 如果current不满足条件，则直接返回一个空对象{}。
 */
export const asyncAwaitForm = async current => {
  return (
    (current &&
      current.validateFieldsAndScroll &&
      (await current.validateFieldsAndScroll((errs, vals) => {
        if (errs) {
          return false;
        }
        return vals;
      }))) ||
    {}
  );
};


/**
 * 产品类型列表常量数组。
 *
 * 该数组包含了一系列产品类型的对象，每个对象有四个属性：
 * key: 产品类型的键值，用于唯一标识产品类型。
 * value: 产品类型的值，通常是一个数字，用于内部逻辑处理。
 * label: 产品类型的显示名称，用于用户界面展示。
 * code: 产品类型的代码，可能是为了与其他系统集成而设计的。
 *
 */
export const productTypeList = [
  { key: 'dsd', value: '23', label: '定时达', code: '23' },
  { key: 'jzld', value: '24', label: '精准零担', code: '24' },
  { key: 'MINIdsdj', value: '95', label: 'MINI电商大件', code: '95' },
  { key: 'zc', value: '220', label: '整车', code: '220' },
  { key: 'phd', value: '270', label: '普惠达', code: '270' },
  { key: 'tc', value: '428', label: '填仓', code: '428' },
  { key: 'MINIdsxj', value: '524', label: 'MINI电商小件', code: '524' },
  { key: 'axd', value: '546', label: '安心达', code: '546' },
];




/**
 * 将吨转换为千克
 *
 * @param tons 吨数
 * @returns 转换后的千克数，以字符串形式返回
 */
export function convertTonsToKilograms(tons) {
  const tonsBN = new BigNumber(tons);
  const kilograms = tonsBN.multipliedBy(1000);
  return kilograms.toNumber();
}

/**
 * 优化列名称
 *
 * @param columns 列数组
 * @returns 优化后的列数组
 */
export function optimizeColumns(columns) {
  const keysToPrefix = [
    "departureSiteName",
    "adjustSiteName",
    "arriveSiteName",
    "createName",
    "createTime",
    "updateName",
    "updateTime",
    "ticketCount",
    "alArriveWeight",
    "remark"
  ];

  return columns.map(column =>
    keysToPrefix.some(key => column.includes(key))
      ? `t|${column}`
      : column
  );
}

/**
 * 获取唯一的下一个站点信息
 *
 * @param adjustDetailVos 调整详情列表
 * @returns 返回一个对象，包含唯一的下一个站点名称和代码，格式为 { nextSiteNameStr: string, nextSiteCodeStr: string }
 */
export function getUniqueNextSites(adjustDetailVos) {
  const nextSiteNames = map(adjustDetailVos, 'nextSiteName');
  const nextSiteCodes = map(adjustDetailVos, 'nextSiteCode');

  const uniqueNextSiteNames = uniq(nextSiteNames);
  const uniqueNextSiteCodes = uniq(nextSiteCodes);

  const nextSiteNameStr = uniqueNextSiteNames.join(', ');
  const nextSiteCodeStr = uniqueNextSiteCodes.join(', ');

  return { nextSiteNameStr, nextSiteCodeStr };
}

/**
 * 聚合数据并生成汇总
 *
 * @param {Array} data - 输入数据数组
 * @returns {Object} 包含每个 centerSiteName 聚合结果和汇总结果
 */
export const aggregateData = data => {
  const result = data.reduce(
    (acc, item) => {
      let {
        centerSiteName = '其他',
        calcWeight = 0,
        adjustNum = 0,
        adjustCalcWeight = 0,
        totalPiece = 0,
      } = item;

      // 将字符串转换为数字，并处理空值
      calcWeight = parseFloat(calcWeight) || parseFloat(adjustCalcWeight) || 0;
      totalPiece = parseInt(totalPiece, 10) || parseInt(adjustNum, 10) || 0;

      if (!acc[centerSiteName]) {
        acc[centerSiteName] = {
          totalWeight: new BigNumber(0),
          totalPiece: 0,
        };
      }

      if (!isNaN(calcWeight)) {
        const roundedWeightInKg = new BigNumber(round(calcWeight, 1));
        const roundedWeightInTons = roundedWeightInKg.dividedBy(1000);
        acc[centerSiteName].totalWeight = acc[centerSiteName].totalWeight.plus(roundedWeightInTons);
      }

      if (!isNaN(totalPiece)) {
        acc[centerSiteName].totalPiece += totalPiece;
      }

      // 更新总计
      acc.total.totalWeight = acc.total.totalWeight.plus(new BigNumber(calcWeight));
      acc.total.totalPiece += totalPiece;

      return acc;
    },
    {
      total: {
        totalWeight: new BigNumber(0),
        totalPiece: 0,
      },
    },
  );
  // 转换 BigNumber 和 totalPiece 为数字
  return Object.fromEntries(
    Object.entries(result).map(([key, value]) => [
      key,
      {
        totalWeight: value.totalWeight.toNumber(),
        totalPiece: data.length,
      },
    ]),
  );
};

// 定义默认状态
export const defaultState = {
  adjustWeightStatus: adjustWeightStatus, // 待调整货量
  auditStatus: auditStatus, // 状态
  queryInfo: {}, // 数据详情
  drawerVisible: false, // 抽屉是否可见
  // 路由申请新增
  leftFilter: {
    adjustSite: [], // 下一站集合
    adjustSiteCode: '', // 下一站分拨编码
    arriveSite: [], // 目的分拨集合
    arriveSiteCode: '', // 目的分拨编码
    goodsType: '', // 产品类型
    maxCalcWeight: null, // 库存最大重量
    minCalcWeight: null, // 库存最小重量
    inputRangeNumber: [null, null],
  }, // 左侧筛选区
  rightOrder: '', // 右侧运单号
  leftFilterTable: [], // 左侧table列表
  leftSelectedRowKeys: '', // 左侧table选择项运单号
  leftSelectedRows: [], // 左侧table选择项订单
  rightFilterTable: [], // 右侧table列表
  rightSelectedRowKeys: '', // 右侧table选择项运单号
  rightSelectedRows: [], // 右侧table选择项订单
  orderList: [], // 订单列表信息
  departureProvince: {}, // 省区信息
  tableLoding: false, // 筛选区table加载状态
  noWaybillNo: [], // 找不到的订单
  requestSelectedRows: [], // 路由申请Table选中项
  requestselectedRowKeys: '',
  onBackRoute: false,
  exportVisible: false, // 导出modal 弹窗
  queryFilter: {}, // 查询参数
  isView: false, // 是否为预览
};

/**
 * 过滤数据，去除空值或空数组
 *
 * @param data 待处理的数据对象
 * @returns 过滤后的数据对象
 */
export const cleanedData = data =>
  omitBy(data, value => {
    return value === '' || (Array.isArray(value) && isEmpty(value));
  });

/**
 * 根据右侧过滤表计算权重
 *
 * @param rightFilterTable 右侧过滤表，数组类型，包含每个项的权重信息
 * @returns 返回计算得到的权重总和，BigNumber类型
 */
// const calcWeightBy = rightFilterTable => {
//   return rightFilterTable.reduce((acc, item) => {
//     // 将 calcWeight 转换为 BigNumber 对象
//     const weight = new BigNumber(item.calcWeight);
//     if (!weight.isNaN()) {
//       return acc.plus(weight);
//     }
//     return acc;
//   }, new BigNumber(0));
// };

// 计算 rightFilterTable 中所有 calcWeight 的总和，去除重复运单号
export const calcWeightBy = table => {
  const uniqueTable = uniqBy(table, 'waybillNo');
  return uniqueTable.reduce((acc, item) => {
    const weight = new BigNumber(item.calcWeight);
    return weight.isNaN() ? acc : acc.plus(weight);
  }, new BigNumber(0));
};

/**
 * 将数组中的每个对象的指定键的值提取出来，连接成一个以逗号分隔的字符串。
 *
 * @param array 数组，包含若干个对象。
 * @param key 字符串，指定要从数组中每个对象中提取的键。
 * @returns 返回连接后的字符串，如果数组不存在或为空，则返回空字符串。
 */
export const siteCodes = (array, key) =>
  array && array.length > 0 ? array.map(item => item[key]).join(',') : '';
